import { useState, useMemo, useCallback, useEffect } from 'react';
import { cdn } from '@shared/utils/cdnAssets/cdn';
import { withWindow } from '@shared/utils/withWindow';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';
import { useBookmarkletController } from '../Bookmarklet/Bookmarklet.controller';
import { useUpdateEventSuggestionMutation } from '@graphql/mutations/adminDashboard.graphql.generated';
import { useGetRegistryOnboardingSuggestionsQuery } from '@graphql/queries/registry.graphql.generated';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { doSuggestionCardInteract } from '../../components/OnboardingSuggestionsWidget/components/OnboardingSuggestions/controller/util';
import { useEventTypeConfig } from '@shared/utils/eventType';

const AddButtonPlaceholder = cdn('joy-web/registry/joy-button/joy-button-placeholder.png');

const INSTALL_BROWSER_BUTTON_IDENTIFIER = 'onbrd-reg-install-browser-button';

interface QuickAddControllerArgs {
  eventId: string;
}

export type Browser = 'Safari' | 'Chrome' | 'Firefox';

export const useQuickAddController = ({ eventId }: QuickAddControllerArgs) => {
  const { source } = useQueryParams();
  const { config } = useEventTypeConfig();
  const { joyButtonInstalled, joyButtonInstallClicked } = useAdminRegistryTelemetry();
  const { getBookmarklet } = useBookmarkletController();
  const isMobileOrTablet = useIsMobileOrTablet();

  const initialBrowserValue = useMemo(
    () =>
      withWindow(global => {
        if (global.navigator.vendor?.indexOf('Apple') > -1) {
          return 'Safari';
        }
        if (global.navigator.vendor?.indexOf('Google') > -1) {
          return 'Chrome';
        }
        return 'Firefox';
      }, 'Safari'),
    [],
  );
  const [browser, setBrowser] = useState<Browser>(initialBrowserValue);

  const { script: bookmarkletScript } = getBookmarklet();

  const toggleBrowser = () => setBrowser(prevState => (prevState === 'Chrome' ? 'Firefox' : 'Chrome'));

  const switchBrowser = (newBrowser: Browser) => setBrowser(newBrowser);

  const handleClick = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      joyButtonInstallClicked(eventId);
    },
    [eventId, joyButtonInstallClicked],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLAnchorElement>) => {
      joyButtonInstallClicked(eventId);

      const img = new Image();
      img.src = AddButtonPlaceholder;
      event.dataTransfer.setDragImage(img, 65, 15);
    },
    [eventId, joyButtonInstallClicked],
  );

  const { data: registryOnboardingSuggestions } = useGetRegistryOnboardingSuggestionsQuery({
    variables: { eventId },
    batchMode: 'fast',
    skip: source !== 'cardAction',
  });
  const installBrowserButtonSuggestion = registryOnboardingSuggestions?.eventById?.suggestion.items.find(suggestion => suggestion.identifier === INSTALL_BROWSER_BUTTON_IDENTIFIER);

  const [updateEventSuggestionMutation] = useUpdateEventSuggestionMutation({
    refetchQueries: ['GetRegistryOnboardingSuggestions'],
  });

  const handleFireEvents = useCallback(() => {
    joyButtonInstalled(eventId);
    if (!isMobileOrTablet && !!installBrowserButtonSuggestion) {
      const { id, stateJSON } = installBrowserButtonSuggestion;
      doSuggestionCardInteract(updateEventSuggestionMutation)({ id, identifier: INSTALL_BROWSER_BUTTON_IDENTIFIER, interaction: 'use', source: 'cardAction', stateJSON });
    }
  }, [eventId, installBrowserButtonSuggestion, isMobileOrTablet, joyButtonInstalled, updateEventSuggestionMutation]);

  const handleDrop = useCallback(
    event => {
      withWindow(global => {
        let topOfContentArea = global.screenY + (global.outerHeight - global.innerHeight);
        if (browser === 'Safari') {
          // for some reason Safari sets the drop Y coordinates lower than the top of the
          // content area even when you drop stuff in the address bar area.
          // its typically 37 to 38 pixels off, but let's just round it to 50 to be sure.
          topOfContentArea += 50;
        }
        const rightOfContentArea = global.screenX + global.outerWidth;

        const verticalDroppedWithinBookmarkBar = event.screenY > global.screenY && event.screenY < topOfContentArea;
        const horizontalDroppedWithinBookmarkBar = event.screenX > global.screenX && event.screenX < rightOfContentArea;

        if (verticalDroppedWithinBookmarkBar && horizontalDroppedWithinBookmarkBar) {
          handleFireEvents();
        }
      });
    },
    [browser, handleFireEvents],
  );

  useEffect(() => {
    withWindow(window => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [browser]);

  return { browser, bookmarkletScript, config, handleClick, handleDragStart, handleDrop, toggleBrowser, switchBrowser };
};
